/* eslint-disable no-console */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  userId?: string;
  sessionId?: string;
  requestId?: string;
  [key: string]: any;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
  stack?: string;
}

class Logger {
  private isDevelopment: boolean;
  private isEnabled: boolean;
  private minLevel: LogLevel;
  private logBuffer: LogEntry[] = [];
  private maxBufferSize = 1000;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.isEnabled = this.isDevelopment || import.meta.env.VITE_ENABLE_LOGGING === 'true';
    this.minLevel = (import.meta.env.VITE_LOG_LEVEL as LogLevel) || 'info';
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.isEnabled) return false;

    const levels: Record<LogLevel, number> = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3,
    };

    return levels[level] >= levels[this.minLevel];
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
    };

    if (context) {
      entry.context = context;
    }

    // Add stack trace for errors
    if (level === 'error') {
      entry.stack = new Error().stack;
    }

    return entry;
  }

  private addToBuffer(entry: LogEntry): void {
    this.logBuffer.push(entry);
    
    // Limit buffer size
    if (this.logBuffer.length > this.maxBufferSize) {
      this.logBuffer = this.logBuffer.slice(-this.maxBufferSize);
    }
  }

  private logToConsole(entry: LogEntry): void {
    const { level, message, context, timestamp } = entry;
    const prefix = `[${timestamp}] ${level.toUpperCase()}:`;

    switch (level) {
      case 'debug':
        console.debug(prefix, message, context);
        break;
      case 'info':
        console.info(prefix, message, context);
        break;
      case 'warn':
        console.warn(prefix, message, context);
        break;
      case 'error':
        console.error(prefix, message, context);
        if (entry.stack) {
          console.error('Stack trace:', entry.stack);
        }
        break;
    }
  }

  private log(level: LogLevel, message: string, context?: LogContext): void {
    if (!this.shouldLog(level)) return;

    const entry = this.formatMessage(level, message, context);
    this.addToBuffer(entry);
    this.logToConsole(entry);

    // Send to monitoring service in production
    if (!this.isDevelopment && level === 'error') {
      this.sendToMonitoring(entry);
    }
  }

  private sendToMonitoring(entry: LogEntry): void {
    // In a real application, you would send this to a monitoring service
    // like Sentry, LogRocket, or your own logging endpoint
    
    try {
      if (window.gtag) {
        window.gtag('event', 'exception', {
          description: entry.message,
          fatal: false,
        });
      }

      // Example: Send to your logging endpoint
      // fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(entry),
      // }).catch(() => {}); // Silently fail to avoid logging loops
    } catch (error) {
      // Silently fail to avoid logging loops
    }
  }

  // Public logging methods
  debug(message: string, context?: LogContext): void {
    this.log('debug', message, context);
  }

  info(message: string, context?: LogContext): void {
    this.log('info', message, context);
  }

  warn(message: string, context?: LogContext): void {
    this.log('warn', message, context);
  }

  error(message: string, error?: Error | unknown, context?: LogContext): void {
    let fullContext = { ...context };
    
    if (error instanceof Error) {
      fullContext = {
        ...fullContext,
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
      };
    } else if (error) {
      fullContext = {
        ...fullContext,
        error: error,
      };
    }

    this.log('error', message, fullContext);
  }

  // Utility methods
  group(label: string): void {
    if (this.isDevelopment) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.isDevelopment) {
      console.groupEnd();
    }
  }

  time(label: string): void {
    if (this.isDevelopment) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.isDevelopment) {
      console.timeEnd(label);
    }
  }

  table(data: any): void {
    if (this.isDevelopment) {
      console.table(data);
    }
  }

  // Performance logging
  performance(operation: string, duration: number, context?: LogContext): void {
    this.debug(`Performance: ${operation} took ${duration}ms`, {
      ...context,
      operation,
      duration,
    });
  }

  // HTTP request logging
  request(method: string, url: string, status?: number, duration?: number): void {
    const message = `${method.toUpperCase()} ${url}${status ? ` - ${status}` : ''}`;
    
    this.debug(message, {
      type: 'http_request',
      method: method.toUpperCase(),
      url,
      status,
      duration,
    });
  }

  // User action logging
  userAction(action: string, context?: LogContext): void {
    this.info(`User action: ${action}`, {
      ...context,
      type: 'user_action',
      action,
    });
  }

  // Feature usage logging
  feature(feature: string, context?: LogContext): void {
    this.info(`Feature used: ${feature}`, {
      ...context,
      type: 'feature_usage',
      feature,
    });
  }

  // Get logs for debugging
  getLogs(): LogEntry[] {
    return [...this.logBuffer];
  }

  // Clear log buffer
  clearLogs(): void {
    this.logBuffer = [];
  }

  // Export logs as JSON
  exportLogs(): string {
    return JSON.stringify(this.logBuffer, null, 2);
  }

  // Set log level dynamically
  setLogLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  // Enable/disable logging
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
  }
}

// Create and export singleton logger instance
export const logger = new Logger();

// Development helpers
if (import.meta.env.DEV) {
  // Make logger available globally for debugging
  (window as any).logger = logger;
  
  // Log environment info
  logger.info('Logger initialized', {
    environment: import.meta.env.MODE,
    logLevel: logger['minLevel'],
    version: import.meta.env.VITE_APP_VERSION || 'unknown',
  });
}

// Export types for external use
export type { LogLevel, LogContext, LogEntry };